import { cloneElement, Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ContentShell from "./ContentShell.jsx";
import { startExpand, startCollapse } from "../lib/flipExpand.js";
import { readUrlParam, writeUrlParam } from "../lib/urlParams.js";
import { resolveDetailCollapsed, detailCollapseParam } from "../lib/dashboardHome.js";

// Both header columns clone the caller's header element to inject a chevron via
// `actions`. A Fragment has a `.type` but accepts only `key`/`children`, so
// cloning one drops the chevron and React warns about the unknown prop — the
// header has to be a component element for the injection to land.
const isCloneableHeader = (header) => Boolean(header?.type) && header.type !== Fragment;

const DETAIL_PANEL_MIN_WIDTH = 360;
const DETAIL_PANEL_HARD_MAX_WIDTH = 1600;
const TRACE_PANEL_MIN_WIDTH = 320;

export const PANE_TOPBAR_CLASS =
  "py-[11px] pb-[0px] max-[900px]:py-[8px] max-[900px]:pb-[0px]";
export const CONTEXT_EYEBROW_CLASS =
  "text-[11px] leading-[1.08] tracking-[0.08em] text-[color:var(--muted)] uppercase";

function clampDetailPanelWidth(value, maxWidth = DETAIL_PANEL_HARD_MAX_WIDTH) {
  return Math.max(
    DETAIL_PANEL_MIN_WIDTH,
    Math.min(maxWidth, DETAIL_PANEL_HARD_MAX_WIDTH, Math.round(value)),
  );
}

function getDefaultDetailPanelWidth(totalWidth, ratio, maxWidth = DETAIL_PANEL_HARD_MAX_WIDTH) {
  return clampDetailPanelWidth(totalWidth * ratio, maxWidth);
}

let sharedDetailPanelWidth = null;
let sharedDetailUserSized = false;

// Drop the session-remembered detail-panel width so the next SplitPage
// mount falls back to the ratio-based default. Used by the "reset panel
// layout" action — pair it with a remount of the SplitPage subtree, as
// a mounted instance holds its own width state.
export function resetSharedDetailPanelWidth() {
  sharedDetailPanelWidth = null;
  sharedDetailUserSized = false;
}

export default function SplitPage({
  defaultDetailRatio = 0.45,
  focusHeader,
  leftContent,
  rightHeader,
  rightContent,
  resetKey,
  // Optional controlled collapse. When defined, drives `detailCollapsed`
  // state — pages that bind drawer visibility to a selection (e.g. the
  // directory browser) pass `!hasSelection` so the panel is exactly
  // mounted while something is selected. Uncontrolled (undefined) leaves
  // the existing URL-persisted toggle behavior alone.
  forceCollapse,
  // Collapse state when ?dcol is absent (no persisted preference). Default
  // open (false). ?dcol persists only the NON-default state, so a page at its
  // default keeps a clean URL.
  defaultCollapsed = false,
  // Reports collapse-state changes up to the page (ScopedPage gates the chat
  // dock on it — collapsed → ⌘K opens the overlay, not the docked panel).
  onCollapsedChange,
  // Optional override for the chevron close button on the right
  // header. When supplied, the chevron calls this instead of (or in
  // addition to) the internal collapse. Pages can use it to clear the
  // selection that controls `forceCollapse`, closing the drawer by
  // unbinding the selection rather than just hiding the panel.
  onCloseDetail,
  // Suppress the appended collapse (">") button on the detail header.
  // The docked chat passes this so its rail carries a single width
  // control (its own expand/collapse toggle) rather than two similar
  // chevrons (the ">" also vanishes when expanded, which read as broken).
  hideDetailClose = false,
  // Optional full-height mode rail pinned to the right edge, OUTSIDE the
  // column area (so it survives either column collapsing to width 0). A
  // render prop — `rail({ collapsed, onToggleCollapsed })`. SplitPage
  // passes the collapse state it owns so the rail can reopen a collapsed
  // panel when a mode is picked, but the collapse/reopen chrome itself
  // stays where it always was — the ">" on the detail tab strip and the
  // "<" on the focus header — since a rail-owned toggle fought with the
  // column-expand path. The rail is purely a mode switcher.
  rail,
}) {
  const initialDetailPanelWidth =
    sharedDetailPanelWidth ??
    (typeof window !== "undefined"
      ? getDefaultDetailPanelWidth(
          Math.max(window.innerWidth - 420, DETAIL_PANEL_MIN_WIDTH),
          defaultDetailRatio,
        )
      : DETAIL_PANEL_MIN_WIDTH);

  const detailResizeRef = useRef({ startX: 0, startWidth: initialDetailPanelWidth });
  const detailUserSizedRef = useRef(sharedDetailUserSized);
  const detailDefaultWidthRef = useRef(initialDetailPanelWidth);
  const shellRef = useRef(null);

  const [detailPanelWidth, _setDetailPanelWidth] = useState(initialDetailPanelWidth);
  const setDetailPanelWidth = (v) => {
    _setDetailPanelWidth((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      sharedDetailPanelWidth = next;
      return next;
    });
  };
  const [detailPanelMaxWidth, setDetailPanelMaxWidth] = useState(DETAIL_PANEL_HARD_MAX_WIDTH);
  // Detail-column open/closed state round-trips through the URL as
  // ?dcol=collapsed so a deep link reproduces whether the operator
  // had the right pane open. Default (open) drops out so fresh URLs
  // stay clean. The URL is read once via useState's initialiser and
  // updates flow through writeUrlParam (history.replaceState) so the
  // collapse animation isn't disturbed by react-router's URL-change
  // re-render cascade.
  const [detailCollapsed, _setDetailCollapsed] = useState(
    () => {
      if (forceCollapse !== undefined) return Boolean(forceCollapse);
      // Explicit persisted state wins; otherwise fall back to the page default.
      return resolveDetailCollapsed(readUrlParam("dcol"), defaultCollapsed);
    },
  );
  const detailCollapsedRef = useRef(detailCollapsed);
  useEffect(() => {
    // When the caller drives collapse externally (e.g. selection-bound
    // drawer), don't pollute the URL — the page is the source of truth
    // and reload state is reconstructed from its own URL keys.
    if (forceCollapse !== undefined) return;
    // Persist only the non-default state so a page at its default stays clean:
    // a default-open page writes ?dcol=collapsed when collapsed, and a
    // default-collapsed one ?dcol=open when opened.
    writeUrlParam("dcol", detailCollapseParam(detailCollapsed, defaultCollapsed));
  }, [detailCollapsed, forceCollapse, defaultCollapsed]);
  useEffect(() => { onCollapsedChange?.(detailCollapsed); }, [detailCollapsed, onCollapsedChange]);

  const detailTransitionId = useRef(0);

  const expandStateRef = useRef(null);

  const setDetailCollapsed = useCallback((v) => {
    const container = shellRef.current;
    detailCollapsedRef.current = v;
    if (!container) { _setDetailCollapsed(v); return; }
    const txId = ++detailTransitionId.current;

    const main = container.querySelector("main");
    if (!main) { _setDetailCollapsed(v); return; }

    const focusCol = main.querySelector('[data-column="focus"]');
    const detailCol = main.querySelector('[data-column="detail"]');

    // A lifted expand-target (the X-ray's ?xcol takeover) and this column
    // FLIP write competing inline styles on the same focus column — exit
    // the takeover instantly (synchronous dispatch) before animating.
    window.dispatchEvent(new CustomEvent("origin-column-expand", { detail: { columnId: "focus", mode: "cancel" } }));

    // Cancel any in-flight animation
    if (expandStateRef.current) expandStateRef.current.cancelled = true;

    if (v && focusCol && detailCol) {
      // ── Collapse: focus expands over detail (column-only FLIP) ──
      // Fade detail via CSS class (!important overrides React inline styles)
      container.classList.add("is-detail-fading");

      expandStateRef.current = startExpand(null, main, null, {
        columnEl: focusCol,
        onComplete: () => {
          if (detailTransitionId.current !== txId) return;
          container.classList.remove("is-detail-fading");
          focusCol.style.left = "0px";
          focusCol.style.right = "0px";
          focusCol.style.removeProperty("width");
          focusCol.style.transition = "none";
          _setDetailCollapsed(true);
          requestAnimationFrame(() => {
            if (detailTransitionId.current !== txId) return;
            focusCol.style.zIndex = "";
            focusCol.style.transition = "";
          });
          expandStateRef.current = null;
        },
      });
    } else if (!v && focusCol) {
      // ── Un-collapse: focus shrinks to reveal detail ──
      // Hide detail via CSS class BEFORE React renders it — prevents opacity:1 flash.
      container.classList.add("is-detail-hidden");
      _setDetailCollapsed(false);

      requestAnimationFrame(() => {
        if (detailTransitionId.current !== txId) return;
        const newDetailCol = main.querySelector('[data-column="detail"]');
        const detailW = newDetailCol?.getBoundingClientRect().width || detailPanelRenderWidthRef.current;
        const targetW = main.getBoundingClientRect().width - detailW;

        startCollapse(expandStateRef.current || { columnEl: focusCol }, main, focusCol, {
          targetWidth: targetW,
          onComplete: () => {
            if (detailTransitionId.current !== txId) return;
            focusCol.style.left = "0px";
            focusCol.style.right = `${detailW}px`;
            expandStateRef.current = null;
          },
        });

        // Remove hidden class after a short delay — React's inline transition
        // (opacity 350ms) kicks in, animating detail from 0 to 1.
        setTimeout(() => {
          if (detailTransitionId.current !== txId) return;
          container.classList.remove("is-detail-hidden");
        }, 80);
      });
    } else {
      _setDetailCollapsed(v);
    }
  }, []);

  const detailPanelRenderWidth = clampDetailPanelWidth(detailPanelWidth, detailPanelMaxWidth);
  // Tracked in a ref so the un-collapse FLIP (a [] useCallback) can fall
  // back to the known width if its rAF measurement reads 0 (which happens
  // when a re-render — e.g. a simultaneous Context→Chat content swap —
  // races the measurement). A 0 there makes the focus column animate to
  // full width and never resize back.
  const detailPanelRenderWidthRef = useRef(detailPanelRenderWidth);
  detailPanelRenderWidthRef.current = detailPanelRenderWidth;

  // ResizeObserver for max width tracking
  useEffect(() => {
    const container = shellRef.current?.querySelector("main");
    if (!container || typeof ResizeObserver === "undefined") return undefined;

    const updateLayoutBounds = () => {
      const rect = container.getBoundingClientRect();
      const nextMaxWidth = Math.max(
        DETAIL_PANEL_MIN_WIDTH,
        Math.round(rect.width - TRACE_PANEL_MIN_WIDTH),
      );
      setDetailPanelMaxWidth(nextMaxWidth);
      detailDefaultWidthRef.current = getDefaultDetailPanelWidth(rect.width, defaultDetailRatio, nextMaxWidth);
      setDetailPanelWidth((current) => clampDetailPanelWidth(current, nextMaxWidth));
    };

    updateLayoutBounds();
    const observer = new ResizeObserver(updateLayoutBounds);
    observer.observe(container);
    return () => observer.disconnect();
  }, [defaultDetailRatio]);

  // Reset on navigation
  useEffect(() => {
    detailUserSizedRef.current = false;
    sharedDetailUserSized = false;
    setDetailPanelWidth(detailDefaultWidthRef.current);
  }, [resetKey]);

  // Controlled collapse — when the parent passes `forceCollapse`, mirror
  // it into internal state on change. This drives "drawer follows
  // selection" pages: forceCollapse=true (no selection) collapses,
  // forceCollapse=false (selection arrived) re-expands, even if the user
  // had previously closed the drawer manually. Skipped when undefined so
  // uncontrolled callers (ScopedView) keep their URL-persisted toggle.
  //
  // useLayoutEffect (not useEffect) is critical here: setDetailCollapsed
  // calls _setDetailCollapsed and then schedules a requestAnimationFrame
  // to measure the freshly-laid-out detail column. A regular useEffect
  // races with that RAF — the React state update isn't flushed in time,
  // so the RAF reads detail.width=0 and the FLIP animates to "fullwidth"
  // (no visible motion, and the focus column ends up wrong). Layout
  // effects run before paint and let React flush the follow-up render
  // synchronously, so by the time the RAF fires the DOM already has
  // the correct detail width.
  useLayoutEffect(() => {
    if (forceCollapse === undefined) return;
    const want = Boolean(forceCollapse);
    if (want !== detailCollapsedRef.current) {
      setDetailCollapsed(want);
    }
  }, [forceCollapse, setDetailCollapsed]);

  // External "reveal the detail panel" intent (e.g. ⌘K surfacing the
  // docked chat). Animated un-collapse via the same path the "Expand
  // panel" chevron uses; a no-op when the panel is already open.
  useEffect(() => {
    const onUncollapse = () => {
      if (detailCollapsedRef.current) setDetailCollapsed(false);
    };
    window.addEventListener("origin-detail-uncollapse", onUncollapse);
    return () => window.removeEventListener("origin-detail-uncollapse", onUncollapse);
  }, [setDetailCollapsed]);

  // External "dismiss the detail panel" intent — the mirror of the above
  // (e.g. the last detail tab was closed and there's nothing left to show).
  // Animated collapse via the same path the "Close panel" control uses; a
  // no-op when already collapsed.
  useEffect(() => {
    const onCollapse = () => {
      if (!detailCollapsedRef.current) setDetailCollapsed(true);
    };
    window.addEventListener("origin-detail-collapse", onCollapse);
    return () => window.removeEventListener("origin-detail-collapse", onCollapse);
  }, [setDetailCollapsed]);

  // Resizer drag
  const handleResizerPointerDown = useCallback((event) => {
    event.preventDefault();
    detailResizeRef.current = { startX: event.clientX, startWidth: detailPanelWidth };
    document.body.classList.add("is-resizing-detail-panel");
    detailUserSizedRef.current = true;
    sharedDetailUserSized = true;

    const container = shellRef.current?.querySelector("main");
    const unlockFns = [];

    let rafId = 0;
    let latestX = event.clientX;

    const onMove = (e) => {
      latestX = e.clientX;
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          const delta = detailResizeRef.current.startX - latestX;
          setDetailPanelWidth(clampDetailPanelWidth(detailResizeRef.current.startWidth + delta, detailPanelMaxWidth));
        });
      }
    };

    const onUp = () => {
      if (rafId) cancelAnimationFrame(rafId);
      for (const fn of unlockFns) fn();
      document.body.classList.remove("is-resizing-detail-panel");
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [detailPanelWidth, detailPanelMaxWidth]);

  // Track column expanded state reactively
  const [columnExpanded, setColumnExpanded] = useState(false);
  useEffect(() => {
    const shell = shellRef.current?.closest?.("[data-shell]");
    if (!shell) return;
    const checkColumn = () => setColumnExpanded(!!shell.dataset.columnExpanded);
    checkColumn();
    if (typeof MutationObserver !== "undefined") {
      const mo = new MutationObserver(checkColumn);
      mo.observe(shell, { attributes: true, attributeFilter: ["data-column-expanded"] });
      const onExpand = (e) => setColumnExpanded(!!e.detail);
      shell.addEventListener("columnexpandchange", onExpand);
      return () => { mo.disconnect(); shell.removeEventListener("columnexpandchange", onExpand); };
    }
  }, []);
  const hideDetailControls = columnExpanded;

  // Build columns
  const columns = [
    {
      id: "focus",
      flex: true,
      minWidth: TRACE_PANEL_MIN_WIDTH,
      // When forceCollapse is on (drawer is bound to a parent selection
      // and there's nothing selected), suppress the expand chevron —
      // there's no detail to show, and clicking it would just bounce
      // back into the collapsed state on the next render.
      header: detailCollapsed && !forceCollapse && isCloneableHeader(focusHeader)
        ? cloneElement(focusHeader, {
            actions: (
              <button
                type="button"
                onClick={() => setDetailCollapsed(false)}
                className="relative z-[2] flex min-h-[38px] w-[34px] shrink-0 items-center justify-center border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
                aria-label="Expand panel"
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 3 5 8l5 5" />
                </svg>
              </button>
            ),
          })
        : focusHeader,
      content: typeof leftContent === "function"
        ? ({ expanded, onToggleExpand, ...rest }) =>
            leftContent({ expanded, onToggleExpand, onExpandDetail: () => { if (detailCollapsedRef.current) setDetailCollapsed(false); if (expanded) onToggleExpand?.(); }, ...rest })
        : () => leftContent,
    },
    ...[{
      id: "detail",
      width: detailCollapsed ? 0 : detailPanelRenderWidth,
      zIndex: 10,
      headerClassName: detailCollapsed ? "hidden" : `grid items-center ${PANE_TOPBAR_CLASS}`,
      // Chevron on the right header. When the caller supplies
      // `onCloseDetail` it owns the close semantic — typically clearing
      // a selection that drives `forceCollapse`, which then collapses
      // the panel through the controlled-collapse effect above. Falls
      // back to the original "just collapse" behavior for uncontrolled
      // callers.
      header: isCloneableHeader(rightHeader) && !hideDetailControls ? cloneElement(rightHeader, {
        // Preserve the header's own trailing actions (e.g. the docked
        // chat's +/PDF) and append the collapse button after them, so
        // header actions sit between the tab overflow and this button.
        // When `hideDetailClose` is set (docked chat), keep only the
        // header's own actions — no appended collapse chevron.
        actions: hideDetailClose ? rightHeader.props.actions : (
          <>
            {rightHeader.props.actions}
            <button
              type="button"
              onClick={onCloseDetail ?? (() => setDetailCollapsed(true))}
              className="relative z-[2] flex min-h-[38px] w-[34px] shrink-0 items-center justify-center border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
              aria-label="Close panel"
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 3l5 5-5 5" />
              </svg>
            </button>
          </>
        ),
      }) : rightHeader,
      // Keep rightContent mounted even when collapsed — collapsing
      // unmounts a deep tree (ContextPanel + per-trace detail subtrees,
      // each of which fires its own data hooks on mount), and the
      // re-mount on re-open is what produces the visible lag. When
      // collapsed, lock the rendered subtree to the previous panel
      // width and clip overflow so the column staying at width:0
      // doesn't cause the children to bleed across the page.
      content: ({ expanded, onToggleExpand }) => (
        <div
          style={{
            height: "100%",
            width: detailCollapsed ? `${detailPanelRenderWidth}px` : "100%",
            overflow: "hidden",
            visibility: detailCollapsed ? "hidden" : "visible",
            pointerEvents: detailCollapsed ? "none" : "auto",
          }}
          aria-hidden={detailCollapsed ? "true" : undefined}
        >
          {rightContent({
            expanded,
            onToggleExpand,
            onCollapseDetail: () => setDetailCollapsed(true),
            expandedPanel: expanded ? "right" : null,
            panelAnimating: false,
            onToggleExpandRight: onToggleExpand,
            onToggleExpandLeft: null,
          })}
        </div>
      ),
    }],
  ];

  // The rail (when present) sits to the right of the column area as a
  // flex sibling. Every layout measurement reads the inner `<main>`
  // (shellRef.current.querySelector("main")), so wrapping the columns in
  // a flex-1 box leaves all the column-width math untouched — the rail
  // just narrows `<main>`. SplitPage owns `detailCollapsed`, so it hands
  // the rail its collapse state + toggle here.
  const railEl = rail
    ? rail({ collapsed: detailCollapsed, onToggleCollapsed: () => setDetailCollapsed(!detailCollapsed) })
    : null;

  // Resizer overlay — positioned based on detail panel width. Hidden
  // when a column is expanded full-width (its stale right-offset
  // position otherwise bleeds a hover line through the expanded
  // surface). Lives inside the column area so its `right` offset is
  // measured from the column edge, not the rail's outer edge.
  const resizer = !detailCollapsed && !columnExpanded ? (
    <div
      className="pointer-events-none absolute inset-0"
      style={{ zIndex: 15 }}
    >
      <div
        data-detail-divider
        className="pointer-events-auto absolute inset-y-0 hidden w-[11px] translate-x-1/2 min-[961px]:block"
        style={{ right: `${detailPanelRenderWidth}px` }}
      >
        <button
          type="button"
          aria-label="Resize detail panel"
          data-detail-resizer
          onPointerDown={handleResizerPointerDown}
          onDoubleClick={() => setDetailPanelWidth(detailDefaultWidthRef.current)}
          className="absolute inset-0 z-10 cursor-col-resize border-0 bg-transparent p-0 touch-none"
        />
      </div>
    </div>
  ) : null;

  if (railEl) {
    return (
      <div ref={shellRef} className="flex h-full min-h-0">
        <div className="relative min-w-0 flex-1">
          <ContentShell columns={columns} />
          {resizer}
        </div>
        {railEl}
      </div>
    );
  }

  return (
    <div ref={shellRef} className="h-full min-h-0">
      <ContentShell columns={columns} />
      {resizer}
    </div>
  );
}
